import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Users, FolderKanban, Layers, ClipboardList, Loader2, Search, Trash2, Eye, Download } from "lucide-react";

const csvCell = (v) => {
  const s = Array.isArray(v) ? v.join("; ") : v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function downloadCsv(filename, columns, rows) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => csvCell(r[c.key])).join(",")).join("\n");
  const csv = `${header}\n${body}`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const TABS = [
  { key: "members", label: "Members", icon: Users },
  { key: "committees", label: "Committees & Programs", icon: FolderKanban },
  { key: "census", label: "Census Rolls", icon: ClipboardList },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [census, setCensus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, id, label }
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, m, c, cn] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/members"), api.get("/committees"), api.get("/admin/census"),
      ]);
      setStats(s.data); setMembers(m.data); setCommittees(c.data); setCensus(cn.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    const endpoints = { member: "/admin/members/", committee: "/admin/committees/", census: "/admin/census/" };
    try {
      await api.delete(`${endpoints[confirm.kind]}${confirm.id}`);
      toast.success(`Removed: ${confirm.label}`);
      setConfirm(null);
      setSelected(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const t = q.toLowerCase();
  const fMembers = members.filter((m) => !t || [m.full_name, m.email, m.member_id, m.region, ...(m.skills || [])].join(" ").toLowerCase().includes(t));
  const fCensus = census.filter((c) => !t || [c.full_name, c.census_id, c.residence_state, c.residence_city, c.email].join(" ").toLowerCase().includes(t));
  const fCommittees = committees.filter((c) => !t || [c.name, c.type, c.proposed_by].join(" ").toLowerCase().includes(t));

  const stamp = new Date().toISOString().slice(0, 10);
  const exportCurrent = () => {
    if (tab === "members") {
      downloadCsv(`ibag-members-${stamp}.csv`, [
        { key: "member_id", label: "Member No." }, { key: "full_name", label: "Name" },
        { key: "email", label: "Email" }, { key: "date_of_birth", label: "Date of Birth" },
        { key: "place_of_birth", label: "Place of Birth" }, { key: "residence", label: "Residence" },
        { key: "region", label: "Region" }, { key: "skills", label: "Skills" },
        { key: "other_skills", label: "Other Skills" }, { key: "committee_interests", label: "Committee Interests" },
        { key: "desired_committees", label: "Proposed Committee" },
      ], fMembers);
    } else if (tab === "committees") {
      downloadCsv(`ibag-committees-${stamp}.csv`, [
        { key: "name", label: "Name" }, { key: "type", label: "Type" },
        { key: "description", label: "Description" }, { key: "member_count", label: "Members" },
        { key: "proposed_by", label: "Proposed By" },
      ], fCommittees);
    } else {
      downloadCsv(`ibag-census-${stamp}.csv`, [
        { key: "census_id", label: "Record No." }, { key: "full_name", label: "Name" },
        { key: "email", label: "Email" }, { key: "residence_state", label: "State" },
        { key: "residence_city", label: "City" }, { key: "year_of_birth", label: "Year of Birth" },
        { key: "heritage", label: "Heritage" },
      ], fCensus);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-2">Office of the Chief Facilitator</div>
        <h1 className="font-serif-display text-5xl font-medium tracking-tight mb-10 text-[#F5F3EF]">National Registry</h1>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
              <StatCard icon={Users} label="Enrolled Members" value={stats.total_members} gold />
              <StatCard icon={FolderKanban} label="Committees" value={stats.total_committees} />
              <StatCard icon={Layers} label="Programs" value={stats.total_programs} />
              <StatCard icon={ClipboardList} label="On the Census" value={stats.total_census} />
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15 mb-6">
              {TABS.map((x) => (
                <button key={x.key} onClick={() => { setTab(x.key); setQ(""); }}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${tab === x.key ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-[#0A0A0A] text-[#F5F3EF] hover:bg-[#141414]"}`}
                  data-testid={`admin-tab-${x.key}`}>
                  <x.icon className="w-4 h-4" /> {x.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="relative w-full max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="rounded-none pl-9 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="admin-search-input" />
              </div>
              <Button onClick={exportCurrent} className="rounded-none bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="admin-export-csv-btn">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>

            {/* MEMBERS */}
            {tab === "members" && (
              <div>
                {stats && (stats.signups_timeline || []).some((p) => p.count > 0) && (
                  <SignupsChart data={stats.signups_timeline} />
                )}
                {stats && (Object.keys(stats.skill_counts || {}).length > 0 || Object.keys(stats.region_counts || {}).length > 0) && (
                  <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {Object.keys(stats.region_counts || {}).length > 0 && (
                      <DistChart title="Members by Region" data={stats.region_counts} total={stats.total_members} color="#5DBB7A" testid="admin-region-chart" />
                    )}
                    {Object.keys(stats.skill_counts || {}).length > 0 && (
                      <DistChart title="Skill Distribution" data={stats.skill_counts} total={stats.total_members} color="#D4AF37" testid="admin-skill-chart" />
                    )}
                  </div>
                )}
                <div className="bg-[#141414] border border-[#D4AF37]/30 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#F5F3EF]/10 hover:bg-transparent">
                      <TableHead className="text-[#A8A29E]">Member No.</TableHead>
                      <TableHead className="text-[#A8A29E]">Name</TableHead>
                      <TableHead className="text-[#A8A29E]">Email</TableHead>
                      <TableHead className="text-[#A8A29E]">Region</TableHead>
                      <TableHead className="text-[#A8A29E] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fMembers.map((m) => (
                      <TableRow key={m.id} className="border-[#F5F3EF]/10 hover:bg-[#D4AF37]/5" data-testid={`admin-member-row-${m.member_id}`}>
                        <TableCell className="font-mono-doc text-xs text-[#D4AF37]">{m.member_id}</TableCell>
                        <TableCell className="font-medium text-[#F5F3EF]">{m.full_name}</TableCell>
                        <TableCell className="text-[#D6D2CA] text-sm">{m.email}</TableCell>
                        <TableCell className="text-[#D6D2CA]">{m.region || "—"}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => setSelected(m)} className="text-[#A8A29E] hover:text-[#F5F3EF]" data-testid={`admin-view-${m.member_id}`}><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: "member", id: m.id, label: m.full_name })} className="text-[#A8A29E] hover:text-[#C8102E]" data-testid={`admin-delete-member-${m.member_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {fMembers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#A8A29E] py-8">No members found.</TableCell></TableRow>}
                  </TableBody>
                </Table>
                </div>
              </div>
            )}

            {/* COMMITTEES */}
            {tab === "committees" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {fCommittees.map((c) => (
                  <div key={c.id} className="bg-[#141414] border border-[#D4AF37]/25 p-6 flex flex-col" data-testid={`admin-committee-${c.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 ${c.type === "program" ? "bg-[#5DBB7A]/15 text-[#5DBB7A]" : "bg-[#C8102E]/15 text-[#F5A3A3]"}`}>{c.type}</span>
                      <span className="text-xs text-[#A8A29E]">{c.member_count} members</span>
                    </div>
                    <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-1">{c.name}</h3>
                    <p className="text-sm text-[#A8A29E] flex-1">{c.description}</p>
                    <p className="text-[11px] text-[#A8A29E]/70 mt-3">Proposed by {c.proposed_by}</p>
                    <Button variant="outline" onClick={() => setConfirm({ kind: "committee", id: c.id, label: c.name })}
                      className="mt-4 rounded-none bg-transparent border-[#C8102E]/50 text-[#F5A3A3] hover:bg-[#C8102E] hover:text-white" data-testid={`admin-delete-committee-${c.id}`}>
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                ))}
                {fCommittees.length === 0 && <p className="text-[#A8A29E] py-8">No committees found.</p>}
              </div>
            )}

            {/* CENSUS */}
            {tab === "census" && (
              <div className="bg-[#141414] border border-[#D4AF37]/30 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#F5F3EF]/10 hover:bg-transparent">
                      <TableHead className="text-[#A8A29E]">Record No.</TableHead>
                      <TableHead className="text-[#A8A29E]">Name</TableHead>
                      <TableHead className="text-[#A8A29E]">State</TableHead>
                      <TableHead className="text-[#A8A29E]">City</TableHead>
                      <TableHead className="text-[#A8A29E] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fCensus.map((c) => (
                      <TableRow key={c.id} className="border-[#F5F3EF]/10 hover:bg-[#D4AF37]/5" data-testid={`admin-census-row-${c.census_id}`}>
                        <TableCell className="font-mono-doc text-xs text-[#D4AF37]">{c.census_id}</TableCell>
                        <TableCell className="font-medium text-[#F5F3EF]">{c.full_name}</TableCell>
                        <TableCell className="text-[#D6D2CA]">{c.residence_state || "—"}</TableCell>
                        <TableCell className="text-[#D6D2CA]">{c.residence_city || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: "census", id: c.id, label: c.full_name })} className="text-[#A8A29E] hover:text-[#C8102E]" data-testid={`admin-delete-census-${c.census_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {fCensus.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#A8A29E] py-8">No census records found.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Member detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto bg-[#141414] border-[#D4AF37]/30 text-[#F5F3EF]">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display text-3xl text-[#F5F3EF]">{selected.full_name}</DialogTitle>
                <p className="font-mono-doc text-xs text-[#A8A29E]">{selected.member_id} &middot; {selected.email}</p>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <Info label="Date of Birth" value={selected.date_of_birth} />
                <Info label="Location of Birth" value={selected.place_of_birth} />
                <Info label="Residence" value={selected.residence} />
                <Info label="Region" value={selected.region} />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#A8A29E] mb-2">Skills</div>
                  <div className="flex flex-wrap gap-2">{(selected.skills || []).map((s) => <span key={s} className="border border-[#D4AF37]/40 text-[#F5F3EF] px-2 py-1 text-xs">{s}</span>)}</div>
                </div>
                {selected.other_skills && <Info label="Other Skills" value={selected.other_skills} />}
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#A8A29E] mb-2">Committee Interests</div>
                  <div className="flex flex-wrap gap-2">{(selected.committee_interests || []).map((s) => <span key={s} className="border border-[#5DBB7A]/40 text-[#5DBB7A] px-2 py-1 text-xs">{s}</span>)}</div>
                </div>
                {selected.desired_committees && <Info label="Proposed New Committee / Program" value={selected.desired_committees} />}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none bg-[#141414] border-[#C8102E]/40 text-[#F5F3EF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#F5F3EF]">Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A8A29E]">
              This will permanently remove <span className="text-[#F5F3EF] font-medium">{confirm?.label}</span> from the registry. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none bg-transparent border-[#F5F3EF]/30 text-[#F5F3EF] hover:bg-[#F5F3EF]/10 hover:text-[#F5F3EF]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="rounded-none bg-[#C8102E] text-white hover:bg-[#9B1B1B]" data-testid="admin-confirm-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function SignupsChart({ data }) {
  const W = 720, H = 180, P = 28;
  const max = Math.max(1, ...data.map((d) => d.count));
  const stepX = (W - P * 2) / Math.max(1, data.length - 1);
  const x = (i) => P + i * stepX;
  const y = (v) => H - P - (v / max) * (H - P * 2);
  const pts = data.map((d, i) => `${x(i)},${y(d.count)}`).join(" ");
  const area = `${P},${H - P} ${pts} ${x(data.length - 1)},${H - P}`;
  const total = data.reduce((s, d) => s + d.count, 0);
  const fmt = (w) => { const dt = new Date(w + "T00:00:00"); return `${dt.getMonth() + 1}/${dt.getDate()}`; };
  return (
    <div className="bg-[#141414] border border-[#D4AF37]/30 p-6 mb-6" data-testid="admin-signups-chart">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Signups Over Time</div>
        <div className="text-xs text-[#A8A29E]">{total} in last 12 weeks</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <polygon points={area} fill="#D4AF37" opacity="0.12" />
        <polyline points={pts} fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => (
          <g key={d.week}>
            <circle cx={x(i)} cy={y(d.count)} r="3" fill="#D4AF37" />
            {(i % 2 === 0 || i === data.length - 1) && (
              <text x={x(i)} y={H - 8} fontSize="10" fill="#A8A29E" textAnchor="middle">{fmt(d.week)}</text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

function DistChart({ title, data, total, color, testid }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6);
  return (
    <div className="bg-[#141414] border border-[#D4AF37]/30 p-6" data-testid={testid}>
      <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-5">{title}</div>
      <div className="space-y-2.5">
        {entries.map(([label, count]) => (
          <div key={label} className="flex items-center gap-4">
            <div className="w-40 text-sm truncate text-[#D6D2CA]">{label}</div>
            <div className="flex-1 bg-[#F5F3EF]/10 h-5">
              <div className="h-5 flex items-center justify-end px-2 text-xs font-semibold text-[#0A0A0A]" style={{ width: `${Math.max((count / Math.max(total, 1)) * 100, 8)}%`, backgroundColor: color }}>{count}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, gold }) {
  return (
    <div className={`p-6 sm:p-8 border ${gold ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]" : "bg-[#141414] border-[#D4AF37]/30 text-[#F5F3EF]"}`}>
      <Icon className={`w-7 h-7 mb-4 ${gold ? "text-[#0A0A0A]" : "text-[#D4AF37]"}`} strokeWidth={1.5} />
      <div className="font-serif-display text-4xl sm:text-5xl font-semibold">{value}</div>
      <div className="text-xs uppercase tracking-wider mt-1 opacity-70">{label}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#A8A29E]">{label}</div>
      <div className="font-medium text-[#F5F3EF]">{value || "—"}</div>
    </div>
  );
}
